import { FormField, Multiselect } from '@amzn/awsui-components-react-v3';
import { MultiselectProps } from '@amzn/awsui-components-react-v3/polaris/multiselect';
import {
    Customer,
    LearningGoal,
} from '../../../../../../smt/interfaces/activity';

/**
 * A customer as returned by get_activity — includes the backend-enriched
 * organization_learning_goals field which is response-only and not stored.
 */
type CustomerWithGoals = Pick<Customer, 'organization_id'> & {
    organization_learning_goals?: LearningGoal[] | null;
};

interface OrganizationGoalsSelectProps {
    customers: CustomerWithGoals[] | null | undefined;
    selectedGoalIds: string[];
    onChange: (goalIds: string[]) => void;
}

/**
 * Merges, deduplicates, and sorts learning goals from all customers by name.
 */
export const getGoalsFromCustomers = (
    customers: CustomerWithGoals[] | null | undefined,
): LearningGoal[] => {
    if (!customers?.length) return [];
    const seenIds = new Set<string>();
    const result: LearningGoal[] = [];
    for (const customer of customers) {
        for (const goal of customer.organization_learning_goals ?? []) {
            if (!seenIds.has(goal.id)) {
                seenIds.add(goal.id);
                result.push(goal);
            }
        }
    }
    return result.sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Returns true if at least one customer has a non-empty organization_id.
 * Used by parent components to decide whether to render the goals section.
 */
export const hasOrganizationId = (
    customers: Array<{ organization_id?: string | null }> | null | undefined,
): boolean => {
    if (!customers?.length) return false;
    return customers.some((c) => !!c.organization_id?.trim());
};

const MAX_DESCRIPTION_LENGTH = 100;

const truncateDescription = (description: string): string =>
    description.length > MAX_DESCRIPTION_LENGTH
        ? `${description.slice(0, MAX_DESCRIPTION_LENGTH)}…`
        : description;

const OrganizationGoalsSelect = ({
    customers,
    selectedGoalIds,
    onChange,
}: OrganizationGoalsSelectProps) => {
    // Goals come directly from the activity response — no separate fetch needed.
    const goals = getGoalsFromCustomers(customers);

    const options: MultiselectProps.Option[] = goals.map((goal) => ({
        label: goal.name,
        value: goal.id,
        description: goal.description
            ? truncateDescription(goal.description)
            : undefined,
        labelTag: goal.languageCode ?? undefined,
        tags: [`ID: ${goal.id}`],
    }));

    const selectedOptions = options.filter((opt) =>
        selectedGoalIds.includes(opt.value as string),
    );

    return (
        <FormField
            label="Organization learning goals"
            description="Optional — select the learning goals associated with this activity."
        >
            <Multiselect
                placeholder="Select learning goals"
                options={options}
                selectedOptions={selectedOptions}
                onChange={(e) => {
                    onChange(
                        e.detail.selectedOptions.map(
                            (opt) => opt.value as string,
                        ),
                    );
                }}
                statusType="finished"
                empty="No goals available for this organization"
                filteringType="auto"
                filteringPlaceholder="Search learning goals"
                filteringAriaLabel="Filter learning goals"
                data-testid="OrganizationGoalsSelect"
            />
        </FormField>
    );
};

export default OrganizationGoalsSelect;
